/**
 * CategorySelectorModal Component
 * Clean, standalone modal for selecting categories
 * Does NOT use CategorySelector - built from scratch for simplicity
 */

import React, { useState, useEffect } from 'react';
import { getCategories, getParentCategories, Category } from '../services/categoryService';

interface CategorySelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCategoryIds: number[];
  onChange: (categoryIds: number[]) => void;
  token: string;
  allCategories: any[];
}

export const CategorySelectorModal: React.FC<CategorySelectorModalProps> = ({
  isOpen,
  onClose,
  selectedCategoryIds,
  onChange,
  token,
  allCategories,
}) => {
  const [parentCategories, setParentCategories] = useState<Category[]>([]);
  const [selectedParent, setSelectedParent] = useState<string | null>(null);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Category[]>([]);

  useEffect(() => {
    if (isOpen && token) {
      loadParentCategories();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (selectedParent && token) {
      loadSubcategories(selectedParent);
    }
  }, [selectedParent, token]);

  useEffect(() => {
    if (searchQuery.trim()) {
      // Filter all categories by search query
      const filtered = allCategories.filter(cat =>
        cat.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, allCategories]);

  const loadParentCategories = async () => {
    try {
      const parents = await getParentCategories(token);
      setParentCategories(parents);
    } catch (error) {
      console.error('Failed to load parent categories:', error);
    }
  };

  const loadSubcategories = async (parentKey: string) => {
    try {
      const subs = await getCategories(token, { parent: parentKey });
      setSubcategories(subs);
    } catch (error) {
      console.error('Failed to load subcategories:', error);
    }
  };

  const toggleCategory = (categoryId: number) => {
    if (selectedCategoryIds.includes(categoryId)) {
      onChange(selectedCategoryIds.filter(id => id !== categoryId));
    } else {
      onChange([...selectedCategoryIds, categoryId]);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay background */}
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 100001
        }}
      />
      {/* Bottom sheet container */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100002,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end'
      }}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{
        width: '100vw',
        maxWidth: '100vw',
        maxHeight: '85vh',
        backgroundColor: 'var(--color-bg-primary)',
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 100002,
        pointerEvents: 'auto',
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        margin: 0
      }}>
        {/* Pinned Header */}
        <div style={{
          backgroundColor: 'var(--color-bg-primary)',
          flexShrink: 0,
          borderBottom: '1px solid var(--color-border)'
        }}>
          {/* Handle bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '8px 0 4px 0'
          }}>
            <div style={{
              width: '36px',
              height: '5px',
              backgroundColor: 'var(--color-border-strong)',
              borderRadius: '3px'
            }} />
          </div>
          
          {/* Title + Done */}
          <div style={{
            padding: '6px 16px 10px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Select Categories</h3>
            <button
              onClick={onClose}
              style={{
                padding: '5px 14px',
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Done
            </button>
          </div>

          {/* Selected Categories */}
          {selectedCategoryIds.length > 0 && (
            <div style={{ padding: '0 16px 8px 16px' }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                padding: '8px',
                backgroundColor: 'var(--color-bg-secondary)',
                borderRadius: '8px',
                minHeight: '32px'
              }}>
                {allCategories
                  .filter(cat => selectedCategoryIds.includes(cat.id))
                  .map(cat => (
                    <span
                      key={cat.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        backgroundColor: '#e0f2fe',
                        border: '1px solid #0ea5e9',
                        borderRadius: '14px',
                        fontSize: 'var(--font-size-sm)',
                        lineHeight: '1.4',
                        color: '#0c4a6e',
                        fontWeight: 500
                      }}
                    >
                      {cat.label}
                      <button
                        onClick={() => onChange(selectedCategoryIds.filter(id => id !== cat.id))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--color-text-faint)',
                          cursor: 'pointer',
                          padding: '0',
                          fontSize: 'var(--font-size-md)',
                          lineHeight: '1',
                          marginLeft: '2px'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div style={{ padding: '0 16px 12px 16px' }}>
            <input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1px solid var(--color-border-strong)',
                borderRadius: '8px',
                fontSize: 'var(--font-size-base)',
                boxSizing: 'border-box',
                outline: 'none',
                backgroundColor: 'var(--color-bg-secondary)'
              }}
            />
          </div>
        </div>

        {/* Scrollable Content */}
        <div style={{ 
          flex: 1, 
          overflow: 'auto', 
          padding: '0 16px 16px 16px',
          minHeight: 0,
          WebkitOverflowScrolling: 'touch'
        }}>
          {/* Search Results */}
          {searchQuery && searchResults.length > 0 ? (
            <div style={{
              border: '1px solid var(--color-border)',
              borderRadius: '12px',
              backgroundColor: 'var(--color-bg-primary)',
              overflow: 'hidden'
            }}>
              {searchResults.map(cat => {
                const isSelected = selectedCategoryIds.includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      width: '100%',
                      backgroundColor: isSelected ? 'var(--color-success-light)' : 'var(--color-bg-primary)',
                      border: 'none',
                      borderBottom: '1px solid var(--color-border-light)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <span style={{ fontSize: '20px', lineHeight: '1', color: '#007bff' }}>
                      {isSelected ? '☑' : '☐'}
                    </span>
                    <span style={{ flex: 1, fontSize: 'var(--font-size-base)', color: 'var(--color-text-primary)' }}>
                      {cat.label}
                    </span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
                      {parentCategories.find(p => p.key === cat.parent_key)?.label}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : searchQuery ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
              No categories found
            </div>
          ) : (
            <>
              {/* Parent Category Buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                {parentCategories.map(parent => (
                  <button
                    key={parent.id}
                    type="button"
                    onClick={() => setSelectedParent(selectedParent === parent.key ? null : parent.key)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: selectedParent === parent.key ? 'var(--color-accent)' : 'var(--color-bg-primary)',
                      color: selectedParent === parent.key ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      fontSize: 'var(--font-size-base)',
                      lineHeight: '1.5',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontWeight: selectedParent === parent.key ? 600 : 400
                    }}
                  >
                    {parent.label}
                  </button>
                ))}
              </div>

              {/* Subcategories */}
              {selectedParent && subcategories.length > 0 && (
            <div style={{
              border: '1px solid var(--color-border)',
              borderRadius: '12px',
              backgroundColor: 'var(--color-bg-primary)',
              overflow: 'hidden'
            }}>
              {subcategories.map(cat => {
                const isSelected = selectedCategoryIds.includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      width: '100%',
                      backgroundColor: isSelected ? 'var(--color-success-light)' : 'var(--color-bg-primary)',
                      border: 'none',
                      borderBottom: '1px solid var(--color-border-light)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <span style={{ fontSize: '20px', lineHeight: '1', color: '#007bff' }}>
                      {isSelected ? '☑' : '☐'}
                    </span>
                    <span style={{ flex: 1, fontSize: 'var(--font-size-base)', color: 'var(--color-text-primary)' }}>
                      {cat.label}
                    </span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
                      {parentCategories.find(p => p.key === cat.parent_key)?.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
            </>
          )}
        </div>
      </div>
      </div>
    </>
  );
};
